import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Globe, ArrowRight, Loader2 } from 'lucide-react';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
export default function ChooseCreationMethodPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showImport, setShowImport] = useState(false);
  const [shotgunUrl, setShotgunUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleManualCreation = () => {
    navigate('/create-festival');
  };

  const handleImportShotgun = async () => {
    if (!shotgunUrl) {
      toast.error('Veuillez entrer un lien ou du contenu.');
      return;
    }

    setIsLoading(true);
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch('/api/parse-shotgun', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ contentOrUrl: shotgunUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erreur serveur ${res.status}`);
      }
      const data = await res.json();
      // La billetterie doit être le lien que l'utilisateur a collé, pas une
      // valeur devinée par Gemini (qui peut halluciner ou en extraire une autre).
      if (shotgunUrl.startsWith('http')) {
        data.billetterie = shotgunUrl;
      }
      toast.success('Informations extraites avec succès !');
      navigate('/create-festival', { state: { prefilledData: data } });
    } catch (e: any) {
      console.error("Erreur d'extraction:", e);
      toast.error('Erreur lors de l\'extraction. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white">
      <FestivalCreationHeader onBack={() => navigate('/my-festivals')} />
      
      <main className="max-w-5xl mx-auto px-6 pt-24 pb-12 flex flex-col items-center justify-center">
        <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mb-4 text-center">
          LANCEZ VOTRE <span className="text-[#FF5E00]">ÉDITION</span>
        </h1>
        <p className="text-gray-500 font-bold uppercase tracking-widest mb-16">
          Comment souhaitez-vous commencer ?
        </p>

        {!showImport ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
            <button 
              onClick={() => setShowImport(true)}
              className="group bg-[#111] border border-white/10 p-8 rounded-[32px] hover:border-[#FF5E00]/50 transition-all text-left"
            >
              <div className="w-16 h-16 bg-[#FF5E00] rounded-2xl flex items-center justify-center mb-8">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black uppercase mb-4">IMPORT SHOTGUN</h2>
              <p className="text-gray-400 mb-8 font-medium">Collez votre lien ou le contenu de la page Shotgun et laissez Festin extraire automatiquement toutes les informations utiles.</p>
              <span className="text-[#FF5E00] font-black uppercase tracking-widest text-sm flex items-center gap-2">
                PLUS RAPIDE 🔥
              </span>
            </button>

            <button 
              onClick={handleManualCreation}
              className="group bg-[#111] border border-white/10 p-8 rounded-[32px] hover:border-white/30 transition-all text-left"
            >
              <div className="w-16 h-16 bg-[#1A1A1A] rounded-2xl flex items-center justify-center mb-8">
                <Globe className="w-8 h-8 text-gray-500 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-black uppercase mb-4">CRÉATION MANUELLE</h2>
              <p className="text-gray-400 mb-8 font-medium">Remplissez le formulaire étape par étape pour configurer votre festival exactement comme vous le souhaitez.</p>
            </button>
          </div>
        ) : (
          <div className="w-full max-w-xl bg-[#111] border border-[#FF5E00]/20 p-8 rounded-[32px]">
             <h2 className="text-2xl font-black uppercase mb-6">IMPORTER SHOTGUN</h2>
             <textarea 
               value={shotgunUrl}
               onChange={(e) => setShotgunUrl(e.target.value)}
               placeholder="Collez ici le lien Shotgun ou tout le contenu de la page..."
               className="w-full h-40 bg-black border border-white/10 rounded-xl px-6 py-4 mb-6 focus:outline-none focus:ring-2 focus:ring-[#FF5E00]"
             />
             <div className="flex gap-4">
               <button onClick={() => setShowImport(false)} className="px-6 py-3 rounded-xl font-bold bg-white/5 hover:bg-white/10">Annuler</button>
               <button 
                 onClick={handleImportShotgun}
                 disabled={isLoading}
                 className="flex-1 px-6 py-3 rounded-xl font-black bg-[#FF5E00] hover:bg-[#FF5E00]/90 transition-all flex items-center justify-center gap-2"
               >
                 {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'EXTRAIRE LES INFOS'}
                 {!isLoading && <ArrowRight className="w-5 h-5" />}
               </button>
             </div>
          </div>
        )}
      </main>
    </div>
  );
}